import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useFieldError, useT } from '@kmgestao/i18n';
import { AuthShell, Button, Input, Label, toast } from '@kmgestao/ui';

import { Brand } from '../../components/Brand.js';
import { mapAuthErrorKey } from '../../features/auth/mapAuthError.js';
import {
  resetPasswordConfirmSchema,
  type ResetPasswordConfirmInput,
} from '../../features/auth/schemas.js';
import { useConfirmPasswordReset } from '../../features/auth/useResetPassword.js';

/**
 * Após o callback trocar o `code` por sessão, o usuário cai aqui para
 * definir a nova senha. Requer sessão ativa (ver `<RequireAuth />`).
 */
export function ResetPasswordConfirmPage() {
  const navigate = useNavigate();
  const confirm = useConfirmPasswordReset();
  const { t } = useT('auth');
  const tError = useFieldError();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordConfirmInput>({
    resolver: zodResolver(resetPasswordConfirmSchema),
    defaultValues: { password: '', passwordConfirmation: '' },
  });

  const passwordError = tError(errors.password);
  const passwordConfirmationError = tError(errors.passwordConfirmation);
  const rootError = tError(errors.root);

  const onSubmit = handleSubmit((values) => {
    confirm.mutate(values, {
      onSuccess: () => {
        toast.success(t('resetConfirm.success'));
        navigate('/dashboard', { replace: true });
      },
      onError: (error) => setError('root', { message: mapAuthErrorKey(error) }),
    });
  });

  return (
    <AuthShell brand={<Brand onDark className="h-20 sm:h-24" />}>
      <div className="space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{t('resetConfirm.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('resetConfirm.subtitle')}</p>
        </div>

        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <div className="space-y-2">
            <Label htmlFor="new-password">{t('resetConfirm.newPassword')}</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              hasError={!!errors.password}
              {...register('password')}
            />
            {passwordError ? (
              <p className="text-sm text-destructive" role="alert">
                {passwordError}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">{t('resetConfirm.confirmNewPassword')}</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              hasError={!!errors.passwordConfirmation}
              {...register('passwordConfirmation')}
            />
            {passwordConfirmationError ? (
              <p className="text-sm text-destructive" role="alert">
                {passwordConfirmationError}
              </p>
            ) : null}
          </div>

          {rootError ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
            >
              {rootError}
            </div>
          ) : null}

          <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('resetConfirm.submitting') : t('resetConfirm.submit')}
          </Button>
        </form>
      </div>
    </AuthShell>
  );
}
